import GraphemeSplitter from 'grapheme-splitter';
import _ from 'lodash';
// components
import { Descendant } from 'slate';

import { formulaUpdateKeys } from '../constants/formulaConstant';
import { FormulaType } from '../constants/formulaType';
import { messageContentUpdateKeys } from '../constants/messageContent.constant';

// constants
import { EditorDetailsModel } from '../context/EditorContext';

import {
    ALL_USERS_AUDIENCE_ID,
    AudienceMainModel,
} from '../models/audience.model';
import {
    CMSFilterModel,
    CMSPlaceholderModel,
    DEFAULT_CMS_TYPE_VALUE,
} from '../models/cms-placeholder.model';
import { CompoundTriggerType } from '../models/compound-trigger.model';
import {
    ElementKind,
    FormulaContentPreviewModel,
    GenericMessagePreviewModel,
    SectionElement,
    SectionType,
    TextElement,
} from '../models/editor.model';
import { TagModel } from '../models/tag.model';
import {
    TimingSlotsModel,
    WEEKDAYS,
    WeekdayAbbreviations,
} from '../models/timing-slots.model';
import {
    ContentCardType,
    EMAIL_LINK_KEYS,
    EMAIL_REQUIRED_KEYS,
    EmailFieldDataModel,
    EmailFieldModifiers,
    FormulaCategory,
    FormulaCreateModel,
    FormulaDetailModel,
    FormulaLabel,
    FormulaSchedulingOptions,
    FormulaStartStopType,
    FormulaTypesData,
    FormulaUpdateBase,
    FormulaViewSetupFormValue,
    FormulasCategoryCountModel,
    MaxCharacterModel,
} from './../models/formula.model';
import { MessageContent } from 'models/message-content.model';

import { ConstraintsFormValues } from '../views/formulaCreate/formulaSettings/MessageTimingConstraints';

import { formatUTCDate, getUTCDate } from './dateTimeHelpers';
// helpers
import {
    convertArrayToObject,
    objectsAreEqual,
    titlecase,
} from './sanitize.helper';
import { getUtcToZonedFormattedDate } from './timezoneHelper';

const splitter = new GraphemeSplitter();

export const getVariantTypeLabelsMap = (formulaLabels: FormulaLabel[]) => {
    const variantTypeLabelsMap = formulaLabels.reduce<{
        [key: string]: FormulaLabel[];
    }>((labelsMap, label) => {
        labelsMap[label.variant_type] = [
            ...(labelsMap[label.variant_type] ?? []),
            label,
        ];
        return labelsMap;
    }, {});
    Object.keys(variantTypeLabelsMap).forEach((item) => {
        if (!variantTypeLabelsMap[item].length) {
            delete variantTypeLabelsMap[item];
        }
    });
    return variantTypeLabelsMap;
};

//This method return boolean that send time exceeded the buffer time or not
export const hasScheduleTimeExceededBufferTime = (
    send_start: string,
    bufferTime: number,
) => {
    const currentDate = new Date();
    const currentUTCDate = getUTCDate(
        currentDate.toISOString().replace('Z', ''),
    );
    const scheduleDate = getUTCDate(send_start, true);
    const minsDifference =
        (currentUTCDate.getTime() - scheduleDate.getTime()) / 60000;
    return minsDifference >= bufferTime;
};

export const getWhatsappTemplate = (structure: Descendant[]): string => {
    let counter: number = 1;
    let template: string = '';

    const parse_slate_node_to_text = (node: Descendant): string => {
        let content: string = '';
        if ((node as TextElement).text) {
            content += (node as TextElement).text;
        } else if (
            [
                ElementKind.variant,
                ElementKind.dataset,
                ElementKind.cms_placeholder,
                ElementKind.link,
            ].includes((node as SectionElement).kind)
        ) {
            content += `{{${counter}}}`;
            counter += 1;
        } else if ((node as SectionElement).children) {
            (node as SectionElement).children.forEach(
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                (item) => (content += parse_slate_node_to_text(item)),
            );
        }
        return content;
    };

    structure.forEach((node) => (template += parse_slate_node_to_text(node)));

    return template;
};

export const renderFormulaChannelTabs = (
    status: FormulaCategory,
    counts?: FormulasCategoryCountModel,
) => {
    const currentCounts = counts ? counts[status] : null;
    return Object.values(FormulaTypesData)
        .filter((type) => {
            return (
                type.standaloneMessages &&
                currentCounts &&
                currentCounts[type.key as FormulaType] > 0
            );
        })
        .sort((a, b) => {
            if (
                currentCounts &&
                currentCounts[a.key as FormulaType] <
                    currentCounts[b.key as FormulaType]
            ) {
                return 1;
            } else {
                return -1;
            }
        })
        .map((type) => ({
            key: type.key,
            name: counts
                ? `${type.title} (${JSON.stringify(
                      counts[status][type.key as FormulaType],
                  )})`
                : type.title,
        }));
};

export const getAudienceFilterChip = (
    audiences?: AudienceMainModel[],
    audience_id?: string,
) => {
    const selectedAudience = audiences?.find((item) => item.id === audience_id);
    if (!selectedAudience) return [];
    return [{ id: selectedAudience?.id, name: selectedAudience?.name }];
};

export const getMaxCharacterFromEditorDetails = (
    editorDetails: EditorDetailsModel,
    extraPlaceholderChar: MaxCharacterModel,
): MaxCharacterModel => {
    let maxChar: MaxCharacterModel = {};
    if (editorDetails?.previews?.length > 0) {
        editorDetails.previews.forEach((editor: GenericMessagePreviewModel) => {
            Object.keys(editor.content).forEach((sectionType) => {
                const currentText =
                    (editor.content as any)[sectionType as SectionType] ?? '';
                const currentCharLength =
                    splitter.countGraphemes(currentText) +
                    ((extraPlaceholderChar as any)[sectionType] ?? 0);
                maxChar = {
                    ...maxChar,
                    [sectionType]: Math.max(
                        maxChar[sectionType as SectionType] ?? 0,
                        currentCharLength,
                    ),
                };
            });
        });
    }
    return maxChar;
};

export const getExtraCharactersOfPlaceholders = (
    editorDetails: EditorDetailsModel,
    cmsPlaceholders: CMSPlaceholderModel[] = [],
): MaxCharacterModel => {
    const cmsPlaceholderObject = convertArrayToObject(cmsPlaceholders, 'id');
    const defaultCmsLength = 20;
    let placeholderChar: MaxCharacterModel = {};
    editorDetails.formula?.structure?.forEach((element: any) => {
        element?.children.forEach((child: any) => {
            if (child.kind === ElementKind.cms_placeholder) {
                let sectionCount =
                    (placeholderChar as any)[element.sectionType] ?? 0;
                const name = cmsPlaceholderObject[child.cmsPlaceholderId]?.name;
                sectionCount += name
                    ? defaultCmsLength - `{{${name}}}`.length
                    : 0;
                placeholderChar = {
                    ...placeholderChar,
                    [element.sectionType]: sectionCount,
                };
            }
        });
    });
    return placeholderChar;
};

export const getSortedAvailableTimeRanges = (
    timingSlots: TimingSlotsModel[],
) => {
    const formattedTimes: string[] = [];

    timingSlots.forEach((item) => {
        formattedTimes.push(`${item.start_time} - ${item.end_time}`);
    });

    const uniqueTimes = [...new Set(formattedTimes)];

    uniqueTimes.sort((a, b) => {
        const [startA] = a.split(' - ');
        const [startB] = b.split(' - ');

        const timeA = startA.split(':').map((n) => parseInt(n));
        const timeB = startB.split(':').map((n) => parseInt(n));

        if (timeA[0] < timeB[0]) return -1;
        if (timeA[0] > timeB[0]) return 1;
        return 0;
    });

    return uniqueTimes;
};

export const getInitialConstraintsValues = (
    availableTimeRanges: string[],
    timingSlots: TimingSlotsModel[],
    formulaTimingSlots?: string[],
) => {
    const data = Object.values(WeekdayAbbreviations).reduce<any>(
        (result, day) => {
            const defaultIsSelected =
                !formulaTimingSlots || formulaTimingSlots.length === 0;

            availableTimeRanges.forEach((time) => {
                const blockId = timingSlots.find(
                    (item) =>
                        item.day_of_week ===
                            Object.keys(WeekdayAbbreviations).find(
                                (item) =>
                                    WeekdayAbbreviations[item as WEEKDAYS] ===
                                    day,
                            ) &&
                        `${item.start_time} - ${item.end_time}` === time,
                )?.id;

                if (blockId) {
                    if (!result.time_constraints[day]) {
                        result.time_constraints[day] = {};
                    }
                    if (!result.time_constraints[day][time]) {
                        result.time_constraints[day][time] = {};
                    }
                    result.time_constraints[day][time] = {
                        id: blockId,

                        isSelected:
                            defaultIsSelected ||
                            formulaTimingSlots?.includes(blockId),
                    };
                }
            });
            return result;
        },
        { time_constraints: {} },
    );
    return data;
};

export const getConstraintsChanges = (
    values: ConstraintsFormValues,
    formulaTimingSlots?: string[],
) => {
    const selectedIds: string[] = [];

    Object.values(values.time_constraints).forEach((times) =>
        Object.values(times).forEach((item) => {
            if (item.isSelected) selectedIds.push(item.id);
        }),
    );

    const isDataSame =
        formulaTimingSlots &&
        selectedIds.length === formulaTimingSlots.length &&
        formulaTimingSlots.every((val) => selectedIds.includes(val));

    if (!isDataSame) {
        return selectedIds;
    }
};

export const getIsOtherDaySelected = (
    timeSlots: ConstraintsFormValues,
    excludedDay: string,
) => {
    let anySelected = false;
    Object.keys(timeSlots.time_constraints).forEach((day) => {
        if (day !== excludedDay && !anySelected) {
            anySelected = !!Object.keys(timeSlots.time_constraints[day]).filter(
                (time) => timeSlots.time_constraints[day][time].isSelected,
            ).length;
        }
    });
    return anySelected;
};

export const getIsOtherTimeSelected = (
    timeSlots: ConstraintsFormValues,
    excludedTime: string,
) => {
    let anySelected = false;
    Object.values(timeSlots.time_constraints).forEach((timeObject) => {
        const otherTimesKeys = Object.keys(timeObject).filter(
            (time) => time !== excludedTime,
        );
        if (!anySelected) {
            anySelected = !!Object.keys(timeObject).filter(
                (time) =>
                    otherTimesKeys.includes(time) &&
                    timeObject[time].isSelected,
            ).length;
        }
    });
    return anySelected;
};

export const getAvailableWeekDays = (timeSlots: TimingSlotsModel[]) =>
    Array.from(new Set([...timeSlots.map((item) => item.day_of_week)]));

export const getAvailableAbbreviatedWeekDays = (
    timeSlots: TimingSlotsModel[],
) => getAvailableWeekDays(timeSlots).map((item) => WeekdayAbbreviations[item]);

export const getFormulaScheduleTimeText = (
    isControlledFormula?: boolean,
    startDate?: string,
    endDate?: string,
    timezone?: string,
) => {
    if (isControlledFormula) {
        return getUtcToZonedFormattedDate(
            timezone as string,
            startDate as string,
        );
    } else {
        return `Starts ${
            startDate ? formatUTCDate(startDate) : 'anytime'
        } - Expires ${endDate ? formatUTCDate(endDate) : 'never'}`;
    }
};

export const isTagsValueChanged = (
    initialTags: string[],
    updatedTags: string[],
) => {
    if (initialTags?.length !== updatedTags?.length) {
        return true;
    }
    return !initialTags.every((tag) => updatedTags.includes(tag));
};

export const isCMSFiltersChanged = (
    initialList: CMSFilterModel[],
    updatedList: CMSFilterModel[],
) => {
    if (initialList.length !== updatedList.length) {
        return true;
    }

    for (const objA of initialList) {
        if (!updatedList.some((objB) => objectsAreEqual(objA, objB))) {
            return true;
        }
    }

    return false;
};

const isLabelValueChanged = (
    newLabels: FormulaLabel[],
    oldLabels: FormulaLabel[],
): boolean => {
    if (newLabels.length !== oldLabels.length) {
        return true;
    }
    newLabels.sort((labelA, labelB) => labelA.label_id - labelB.label_id);
    oldLabels.sort((labelA, labelB) => labelA.label_id - labelB.label_id);
    return newLabels.some(
        (newLabel, index) => newLabel.label_id !== oldLabels[index].label_id,
    );
};

export const getEditFormulaFormValues = (
    initialValues: FormulaViewSetupFormValue,
    values: FormulaViewSetupFormValue,
) => {
    const isNameChanged = initialValues?.name !== values.name;
    let cmsValue: string | number | null = values.cms_type?.value;
    const updatedAudienceId =
        values.audience_id === ALL_USERS_AUDIENCE_ID
            ? null
            : values.audience_id!;
    const isCMSTypeChanged = initialValues?.cms_type?.value !== cmsValue;
    if (values.cms_type?.value === DEFAULT_CMS_TYPE_VALUE) {
        cmsValue = null;
    }
    const isTagsChanged = isTagsValueChanged(
        initialValues?.tags?.map((tag) => tag.value) ?? [],
        values?.tags?.map((tag) => tag.value) ?? [],
    );
    const tags = values?.tags?.map((tag) => tag.value) ?? [];
    const isAudienceChanged = initialValues.audience_id !== values.audience_id;
    const isCompoundTriggerChanged =
        initialValues.compound_trigger?.value !==
        values.compound_trigger?.value;
    const compoundTriggerId = values?.compound_trigger?.value
        ? values.compound_trigger?.value
        : null;
    const formulaName = values.name;
    const isCmsFilterChanged = isCMSFiltersChanged(
        initialValues.cms_filters,
        values.cms_filters,
    );
    const isStartTimeChanged = values.send_start !== initialValues.send_start;
    const isStartDateChanged =
        values.send_start_date !== initialValues.send_start_date;
    const isLabelChanged = isLabelValueChanged(
        values.labels || [],
        initialValues.labels || [],
    );

    const isStopDateChanged =
        values.send_stop_date !== initialValues.send_stop_date;
    const isStopTimeChanged = values.send_stop !== initialValues.send_stop;
    const isTimeZoneChanged = values.timezone !== initialValues.timezone;

    const isTimeSlotsIdsChange = !_.isEqual(
        _.sortBy(initialValues.time_slot_ids),
        _.sortBy(values.time_slot_ids),
    );

    const isSchedulingTypeChange =
        values.scheduling_type !== initialValues.scheduling_type;
    const isTopicIdChanged = values.topic_id !== initialValues.topic_id;
    const isRewardFunctionIdChanged =
        values.reward_function_id !== initialValues.reward_function_id;

    return {
        tags,
        formulaName,
        compoundTriggerId,
        isNameChanged,
        isCMSTypeChanged,
        isTagsChanged,
        isAudienceChanged,
        cmsValue,
        updatedAudienceId,
        isCompoundTriggerChanged,
        isCmsFilterChanged,
        isStartTimeChanged,
        isLabelChanged,
        isStartDateChanged,
        isStopDateChanged,
        isStopTimeChanged,
        isTimeZoneChanged,
        isTimeSlotsIdsChange,
        isSchedulingTypeChange,
        isTopicIdChanged,
        isRewardFunctionIdChanged,
    };
};

export const getFormulaContentValidation = (
    sample: FormulaContentPreviewModel,
    formulaType: FormulaType,
    contentCardType?: ContentCardType,
) => {
    switch (formulaType) {
        case FormulaType.mobilePush:
        case FormulaType.webPush:
        case FormulaType.surface:
            return (
                !!sample?.content?.body?.trim() &&
                !!sample?.content?.header?.trim()
            );
        case FormulaType.whatsapp:
        case FormulaType.sms:
            return !!sample?.content?.message;
        case FormulaType.email:
            return !!sample?.content?.subject || !!sample?.content?.email_body;
        case FormulaType.contentCards:
            if (
                [
                    ContentCardType.TextOnly,
                    ContentCardType.ImageAndText,
                ].includes(contentCardType as ContentCardType)
            ) {
                return !!sample?.content.body;
            } else {
                return !!sample?.image_url;
            }
    }
};

export const getFormulaSchedulingType = (
    timingSlots: TimingSlotsModel[],
    data?: FormulaDetailModel,
) => {
    if (!data) return FormulaSchedulingOptions.AUTOMATED;
    if (data.is_controlled_message) return FormulaSchedulingOptions.CONTROLLED;
    if (
        data.send_start_date ||
        data.send_stop_date ||
        (data.time_slot_ids?.length !== timingSlots?.length &&
            data.time_slot_ids?.length)
    )
        return FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS;
    return FormulaSchedulingOptions.AUTOMATED;
};

export const truncatePreviewHeading = (
    heading: string,
    truncationCount: number,
) =>
    heading?.length > truncationCount
        ? heading.slice(0, truncationCount) + '...'
        : heading;

export const getApproveButtonTooltipMessage = (
    sample: FormulaContentPreviewModel,
    formulaType: FormulaType,
) => {
    let sectionTypes: SectionType[] = [];
    switch (formulaType) {
        case FormulaType.mobilePush:
        case FormulaType.webPush:
            if (
                !sample?.content?.body?.trim() ||
                !sample?.content?.header?.trim()
            ) {
                sectionTypes = [SectionType.header, SectionType.body];
            }
            break;
        case FormulaType.whatsapp:
        case FormulaType.sms:
        case FormulaType.email:
            break;
    }
    return sectionTypes.length
        ? `One of your message alternates has an empty ${sectionTypes.join(
              ' and/or ',
          )}`
        : '';
};

export const getEmailFieldData = (sectionType: string): EmailFieldDataModel => {
    if (!sectionType) {
        return {
            label: '',
            isRequired: false,
            isMultiLine: false,
            hideEmoji: false,
            hideCmsPlaceholder: false,
            allowVariantCreation: false,
        };
    }
    const [fieldName, ...modifiers] = sectionType.split('--');
    let label = titlecase(fieldName.replace(/_/g, ' ')) ?? '';
    const isLinkType =
        modifiers.includes(EmailFieldModifiers.link) ||
        EMAIL_LINK_KEYS.includes(fieldName);

    return {
        label,
        isRequired:
            modifiers.includes(EmailFieldModifiers.required) ||
            EMAIL_REQUIRED_KEYS.includes(fieldName),
        isMultiLine: modifiers.includes(EmailFieldModifiers.multiline),
        hideEmoji: isLinkType,
        hideCmsPlaceholder: isLinkType,
        allowVariantCreation: !isLinkType,
    };
};

export const getFormulaStartEndDate = (
    formula: FormulaDetailModel | null,
): FormulaStartStopType => {
    if (formula) {
        if (formula.is_controlled_message) {
            return {
                send_start: formula.send_start ?? undefined,
                send_stop: undefined,
            };
        }
        return {
            send_start: formula.send_start_date ?? undefined,
            send_stop: formula.send_stop_date ?? undefined,
        };
    }
    return {
        send_start: undefined,
        send_stop: undefined,
    };
};

export const showImageAlternates = (
    formulaType: FormulaType,
    element: any,
): boolean => {
    if (
        formulaType &&
        [
            FormulaType.mobilePush,
            FormulaType.email,
            FormulaType.contentCards,
        ].includes(formulaType)
    ) {
        const { sectionType = '', kind } = element;
        const [, ...modifiers] = sectionType?.split('--') ?? [];
        const isImage = modifiers?.includes(EmailFieldModifiers.image);
        const isImageElement =
            [FormulaType.mobilePush, FormulaType.contentCards].includes(
                formulaType,
            ) && kind === ElementKind.image;
        const isEmailAndImageElement =
            formulaType === FormulaType.email && isImage;
        return isImageElement || isEmailAndImageElement;
    }
    return false;
};

export const getContentSetupValidation = (
    formulaType: FormulaType,
    formula?: FormulaDetailModel,
) => {
    switch (formulaType) {
        case FormulaType.email:
            return (
                !!formula?.name &&
                !!formula?.additional_metadata?.from_ &&
                !!formula?.additional_metadata?.email_template_id &&
                !!formula.reward_function_id
            );
        case FormulaType.contentCards:
            return !!(
                formula?.content_card_type &&
                formula?.name &&
                formula.reward_function_id
            );
        default:
            return !!formula?.name && !!formula.reward_function_id;
    }
};

export const disableWhenRTTrigger = (
    triggerType: CompoundTriggerType = CompoundTriggerType.AAMPE_OPTIMISED,
    allowResponsiveFormulas: boolean = false,
) => {
    const isNotValidated =
        (triggerType === CompoundTriggerType.SEND_IMMEDIATELY ||
            triggerType === CompoundTriggerType.SEND_WITH_DELAY) &&
        !allowResponsiveFormulas;
    return {
        isNotValidated,
        buttonTooltipText: isNotValidated
            ? 'Real time events are not integrated yet. Contact team (support@aampe.com) for more info'
            : '',
    };
};

export const renderPerformanceChannelTabs = (
    status: FormulaCategory[],
    counts?: FormulasCategoryCountModel,
) => {
    const combinedCounts: { [key in FormulaType]?: number } = {};

    status.forEach((category) => {
        const currentCounts = counts ? counts[category] : null;
        Object.values(FormulaTypesData).forEach((type) => {
            if (currentCounts && currentCounts[type.key as FormulaType] > 0) {
                const count = currentCounts[type.key as FormulaType];
                if (combinedCounts[type.key as FormulaType]) {
                    combinedCounts[type.key as FormulaType]! += count;
                } else {
                    combinedCounts[type.key as FormulaType] = count;
                }
            }
        });
    });

    return Object.values(FormulaTypesData)
        .filter(
            (type) =>
                combinedCounts[type.key as FormulaType] &&
                combinedCounts[type.key as FormulaType]! > 0,
        )
        .sort((a, b) => {
            return (
                (combinedCounts[b.key as FormulaType] || 0) -
                (combinedCounts[a.key as FormulaType] || 0)
            );
        })
        .map((type) => ({
            key: type.key,
            name: `${type.title} (${combinedCounts[type.key as FormulaType]})`,
        }));
};

export const updateEditorDetailsFromFormikValues = (
    formikValues: FormulaViewSetupFormValue,
    setEditorDetails: (value: any) => void,
) => {
    setEditorDetails((prev: EditorDetailsModel) => ({
        ...prev,
        formula: {
            ...prev.formula,
            send_start: formikValues?.send_start,
            send_stop: formikValues?.send_stop,
            send_start_date: formikValues?.send_start_date,
            send_stop_date: formikValues?.send_stop_date,
            is_controlled_message: formikValues?.is_controlled_message,
            timezone: formikValues?.timezone,
            name: formikValues?.name,
            tags: formikValues?.tags?.map((tag) => ({
                name: tag.label,
                id: tag.value,
            })) as TagModel[],
            audiences: [{ audience_id: formikValues?.audience_id }],
            compound_trigger: {
                id: formikValues?.compound_trigger?.value,
                name: formikValues?.compound_trigger?.label,
            },
            labels: formikValues?.labels,
        } as FormulaDetailModel,
    }));
};

/**
 * This method is used to get the formula fields to update
 * @param formula
 * @returns Partial<T>
 */
const getFormulaFieldsToUpdate = <
    T extends Partial<FormulaCreateModel | FormulaUpdateBase>,
>(
    formula: T,
): Partial<T> => {
    return formulaUpdateKeys.reduce<Partial<T>>((acc, key) => {
        if (key in formula) {
            acc[key as keyof T] = formula[key as keyof T];
        }
        return acc;
    }, {});
};

/**
 * This method is used to get the message content fields to update
 * @param formula
 * @returns Partial<T>
 */
const getMessageContentFieldsToUpdate = <
    T extends Partial<FormulaCreateModel | FormulaUpdateBase>,
>(
    formula: T,
): Partial<T> => {
    return messageContentUpdateKeys.reduce<Partial<T>>((acc, key) => {
        if (key in formula) {
            acc[key as keyof T] = formula[key as keyof T];
        }
        return acc;
    }, {});
};

/**
 * This method is used to get the separate formula fields to update
 * @param formula
 * @returns Dictionary of formula and message content fields to update
 */
export const getSeparateFormulaFieldsToUpdate = <
    T extends Partial<FormulaCreateModel | FormulaUpdateBase>,
>(
    formula: T,
): { formula?: Partial<T>; messageContent?: Partial<T> } => {
    const formulaFields = getFormulaFieldsToUpdate(formula);
    const messageContentFields = getMessageContentFieldsToUpdate(formula);
    return {
        formula:
            Object.keys(formulaFields).length > 0 ? formulaFields : undefined,
        messageContent:
            Object.keys(messageContentFields).length > 0
                ? messageContentFields
                : undefined,
    };
};

/**
 * This method is used to override the formula fields with the message content fields
 * @param formula
 * @param messageContent
 * @returns FormulaDetailModel
 */
export const overrideFormulaFieldsWithMessageContent = <
    T extends FormulaDetailModel,
>(
    formula: T,
    messageContent?: MessageContent,
): T => {
    if (!messageContent) return formula;
    const { id, ...rest } = messageContent;
    return {
        ...formula,
        ...rest,
        messageContentId: id,
    };
};

export const getRandom = <T>(arr: T[]): T =>
    arr[Math.floor(Math.random() * arr.length)];
